/**
 * ============================================================
 * assets/gift-cart-manager.js
 * ============================================================
 * Load file này global trong layout/theme.liquid, SAU khi
 * gift-bar-config.liquid đã render (để window.GiftBarConfig tồn tại):
 *
 *   {% render 'gift-bar-config' %}
 *   <script src="{{ 'gift-cart-manager.js' | asset_url }}" defer></script>
 *
 * Nguyên lý:
 * - KHÔNG lưu state riêng (localStorage/session). Mỗi lần cần
 *   quyết định, đọc lại /cart.js mới nhất rồi tính toán lại từ đầu.
 * - needNotebook / needPillorg = OR trên toàn bộ dòng cart đang
 *   qualify (pack >= 2 hoặc >= 3 + có subscription), KHÔNG cộng dồn
 *   theo số dòng/số lượng -> tối đa 1 dòng gift mỗi loại trong cart.
 * - Gift line được đánh dấu bằng line item property `_gift: 'true'`
 *   + `_gift_type: 'notebook' | 'pillorg'` để nhận diện & dọn dẹp.
 * - Bất kỳ request nào ghi vào cart (/cart/add.js, /cart/change.js,
 *   /cart/update.js) - dù gọi từ theme, từ Appstle hay từ đâu khác -
 *   đều được bắt qua fetch interceptor và tự động trigger reconcile
 *   ngay sau khi request đó xong.
 * ============================================================
 */
(function () {
  if (!window.GiftBarConfig) {
    console.warn('[gift-bar] window.GiftBarConfig chưa được render, bỏ qua cart manager.');
    return;
  }

  var CFG = window.GiftBarConfig;

  if (!CFG.targetProductId) {
    // Chưa config target product trong Theme Settings -> tắt tính năng, không làm gì cả
    return;
  }

  var isReconciling = false;
  var pendingReconcile = false;
  var reconcileTimer = null;

  // -- Parse pack number từ variant_title của cart item (vd: "2 Pack") ----
  function parsePack(variantTitle) {
    var t = (variantTitle || '').toLowerCase();
    if (t.indexOf('3 pack') !== -1) return 3;
    if (t.indexOf('2 pack') !== -1) return 2;
    if (t.indexOf('1 pack') !== -1) return 1;
    return 0;
  }

  function getUrlVariantId() {
    try {
      var params = new URLSearchParams(window.location.search);
      var v = params.get('variant');
      return v ? Number(v) : null;
    } catch (e) {
      return null;
    }
  }

  // Sửa 1 object đã parse (dạng {id, selling_plan} hoặc {items: [...]})
  // -> trả về true nếu có thay đổi
  function sanitizeParsedBody(parsed, urlVariantId) {
    var changed = false;

    function stripIfMismatch(item) {
      if (item && item.selling_plan && Number(item.id) !== urlVariantId) {
        console.warn(
          '[gift-bar] Gỡ selling_plan=' +
            item.selling_plan +
            ' bị leak từ URL sang variant ' +
            item.id +
            ' (không thuộc plan này).'
        );
        delete item.selling_plan;
        changed = true;
      }
    }

    if (Array.isArray(parsed.items)) {
      parsed.items.forEach(stripIfMismatch);
    } else if (parsed.id) {
      stripIfMismatch(parsed);
    }

    return changed;
  }

  // Case 1: body là JSON string, vd fetch(url, { body: JSON.stringify({...}) })
  function sanitizeJsonStringBody(opts, urlVariantId) {
    if (!opts || typeof opts.body !== 'string') return false;
    var parsed;
    try {
      parsed = JSON.parse(opts.body);
    } catch (e) {
      return false; // không phải JSON string thật (có thể là url-encoded) - bỏ qua
    }
    if (sanitizeParsedBody(parsed, urlVariantId)) {
      opts.body = JSON.stringify(parsed);
      return true;
    }
    return false;
  }

  // Case 2: body là FormData, vd fetch(url, { body: new FormData(formEl) })
  function sanitizeFormDataBody(opts, urlVariantId) {
    if (!opts || typeof FormData === 'undefined' || !(opts.body instanceof FormData)) {
      return false;
    }
    var fd = opts.body;
    var id = fd.get('id');
    var sellingPlan = fd.get('selling_plan');
    if (sellingPlan && id && Number(id) !== urlVariantId) {
      console.warn(
        '[gift-bar] Gỡ selling_plan=' + sellingPlan + ' khỏi FormData add request (variant ' + id + ' không thuộc plan này).'
      );
      fd.delete('selling_plan');
      return true;
    }
    return false;
  }

  // Case 3: fetch(new Request(url, {...})) - body nằm trong Request, cần clone
  // để đọc (Request body chỉ đọc được 1 lần), rồi dựng lại Request mới nếu có sửa.
  // Trả về Request mới (hoặc null nếu không cần đổi / không đọc được).
  async function sanitizeRequestObject(request, urlVariantId) {
    try {
      var cloned = request.clone();
      var contentType = (request.headers.get('content-type') || '').toLowerCase();

      if (contentType.indexOf('application/json') !== -1) {
        var parsed = await cloned.json();
        if (sanitizeParsedBody(parsed, urlVariantId)) {
          return new Request(request.url, {
            method: request.method,
            headers: request.headers,
            body: JSON.stringify(parsed),
            credentials: request.credentials,
            mode: request.mode,
            cache: request.cache,
            redirect: request.redirect
          });
        }
      }
      // FormData trong Request object khó parse lại đúng multipart boundary an toàn
      // trên mọi trình duyệt -> nếu gặp case này, chỉ log cảnh báo, không sửa.
      else if (contentType.indexOf('form-data') !== -1) {
        console.warn(
          '[gift-bar] /cart/add.js gửi qua Request object với FormData - chưa tự sanitize được case này, cần báo dev kiểm tra thủ công nếu vẫn lỗi.'
        );
      }
    } catch (e) {
      console.warn('[gift-bar] Không đọc được Request body để sanitize:', e);
    }
    return null;
  }

  // -- Tính needNotebook / needPillorg dựa trên TOÀN BỘ cart hiện tại -----
  function computeRequired(cart) {
    var needNotebook = false;
    var needPillorg = false;

    cart.items.forEach(function (item) {
      if (item.product_id !== CFG.targetProductId) return;
      if (!item.selling_plan_allocation) return; // one-time purchase -> không tính

      var pack = parsePack(item.variant_title);
      if (pack >= 2) needNotebook = true;
      if (pack >= 3) needPillorg = true;
    });

    return { needNotebook: needNotebook, needPillorg: needPillorg };
  }

  function findGiftLine(cart, type) {
    return cart.items.find(function (item) {
      return (
        item.properties &&
        item.properties._gift === 'true' &&
        item.properties._gift_type === type
      );
    });
  }

  async function reconcile() {
    // Nếu đang có 1 lần reconcile chạy dở, đánh dấu cần chạy lại sau khi xong
    // thay vì chạy chồng (tránh 2 request cùng sửa cart 1 lúc gây race condition)
    if (isReconciling) {
      pendingReconcile = true;
      return;
    }
    isReconciling = true;

    try {
      var cartRes = await fetch('/cart.js');
      var cart = await cartRes.json();
      var required = computeRequired(cart);

      var notebookLine = findGiftLine(cart, 'notebook');
      var pillorgLine = findGiftLine(cart, 'pillorg');

      var toAdd = [];
      var toRemoveKeys = [];

      if (required.needNotebook && !notebookLine && CFG.notebook.variantId) {
        toAdd.push({
          id: CFG.notebook.variantId,
          quantity: 1,
          properties: {
            _gift: 'true',
            _gift_type: 'notebook',
            _gift_source: 'gift-progress-bar'
          }
        });
      }
      if (!required.needNotebook && notebookLine) {
        toRemoveKeys.push(notebookLine.key);
      }

      if (required.needPillorg && !pillorgLine && CFG.pillorg.variantId) {
        toAdd.push({
          id: CFG.pillorg.variantId,
          quantity: 1,
          properties: {
            _gift: 'true',
            _gift_type: 'pillorg',
            _gift_source: 'gift-progress-bar'
          }
        });
      }
      if (!required.needPillorg && pillorgLine) {
        toRemoveKeys.push(pillorgLine.key);
      }

      // Không có gì thay đổi -> dừng sớm, không gọi API thừa
      if (toAdd.length === 0 && toRemoveKeys.length === 0) {
        return;
      }

      // Xoá trước (nếu có) để tránh trường hợp cart tạm thời có cả 2 gift
      // cùng lúc khi user vừa chuyển từ 3-pack -> 2-pack
      for (var i = 0; i < toRemoveKeys.length; i++) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: toRemoveKeys[i], quantity: 0 })
        });
      }

      if (toAdd.length > 0) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: toAdd })
        });
      }

      // Impact (Maestrooo theme) yêu cầu dispatch đúng event "cart:refresh"
      // trên document.documentElement với bubbles:true để force update UI
      // (cart count, cart drawer...). Đây là event chuẩn của theme, không phải
      // tự đặt tên - xem: https://support.maestrooo.com/article/764
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true })
      );
    } catch (e) {
      console.warn('[gift-bar] reconcile error:', e);
    } finally {
      isReconciling = false;
      if (pendingReconcile) {
        pendingReconcile = false;
        reconcile();
      }
    }
  }

  function scheduleReconcile(delay) {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcile, delay || 150);
  }

  // -- Fetch interceptor: bắt MỌI request ghi vào cart, từ bất kỳ nguồn nào --
  var originalFetch = window.fetch;
  window.fetch = function (input, opts) {
    var isRequestObj = typeof Request !== 'undefined' && input instanceof Request;
    var url = isRequestObj ? input.url : input;
    var isCartWrite =
      typeof url === 'string' && /\/cart\/(add|change|update)\.js/.test(url);
    var isCartAdd = typeof url === 'string' && /\/cart\/add\.js/.test(url);
    var urlVariantId = getUrlVariantId();

    // Vì việc sanitize Request object là async (phải clone + đọc body), toàn bộ
    // fetch wrapper phải trả về 1 Promise thống nhất - dùng Promise.resolve() để
    // xử lý đồng bộ cho case JSON string/FormData (không cần await) lẫn bất đồng
    // bộ cho case Request object trong cùng 1 luồng.
    var prep = Promise.resolve();

    if (isCartAdd && urlVariantId) {
      if (isRequestObj) {
        prep = sanitizeRequestObject(input, urlVariantId).then(function (newRequest) {
          if (newRequest) input = newRequest;
        });
      } else if (opts) {
        // Thử JSON string trước, không thay đổi gì thì thử FormData
        var didJson = sanitizeJsonStringBody(opts, urlVariantId);
        if (!didJson) sanitizeFormDataBody(opts, urlVariantId);
      }
    }

    var resultPromise = prep.then(function () {
      return isRequestObj ? originalFetch.call(window, input) : originalFetch.call(window, input, opts);
    });

    if (isCartWrite) {
      // Chỉ reconcile khi request GỐC thành công. Nếu request đó lỗi (vd 422 từ
      // 1 nguồn khác như quick-add widget), không cần chạy reconcile vì cart
      // chưa thực sự đổi gì - tránh side-effect/network call thừa khi cart
      // đang ở trạng thái lỗi.
      resultPromise
        .then(function (response) {
          if (response && response.ok) {
            scheduleReconcile(200);
          } else if (response) {
            response
              .clone()
              .json()
              .then(function (body) {
                console.warn('[gift-bar] Bỏ qua reconcile vì request cart write lỗi:', url, body);
              })
              .catch(function () {});
          }
        })
        .catch(function () {
          // Lỗi network (không phải lỗi HTTP status) - bỏ qua, không reconcile
        });
    }

    return resultPromise;
  };

  // -- Safety net: reconcile khi load trang bất kỳ (cart page, cart drawer mở
  // qua link trực tiếp, hoặc cart bị sửa từ nơi không dùng fetch, vd XHR) --
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scheduleReconcile(300);
    });
  } else {
    scheduleReconcile(300);
  }

  // Expose để debug / gọi thủ công từ nơi khác nếu cần
  window.GiftBarReconcile = reconcile;
})();